// Lo PURO de "organizar muchas fotos en productos" desde el chat —
// ADR-2026-09-08-el-chat-trabaja-en-lote.
//
// La carga masiva de Mi marca ya sabe agrupar fotos por producto con IA
// (`POST /v1/products/group-images`, ADR-2026-08-28-agrupar-imagenes-ia) y crear
// productos. Acá está la parte que la tarjeta del agente necesita y que se prueba sin
// browser: convertir los grupos en una PROPUESTA (qué productos, con qué fotos y qué
// nombre), la regla de oro heredada de que NINGUNA foto se pierde, el límite de productos
// por carga del plan, y el resumen que vuelve al agente.

import { MAX_IMAGENES_POR_PRODUCTO } from '../../../lib/limiteImagenesProducto';
import type { GrupoIaRespuesta } from '../../../utils/bulkUnits';

/** Tope de fotos de un lote: 20 productos (el máximo por carga de un plan pago) × 4. */
export const MAX_FOTOS_LOTE = 80;

export interface FotoLote {
  /** Id del front (`f0`, `f1`…): es lo que viaja al agrupado y vuelve en los grupos. */
  id: string;
  nombre: string;
}

export interface ProductoPropuesto {
  id: string;
  /** Nombre sugerido por la IA o editado; vacío = "Nuevo producto N" al mostrar y nombre
   *  por IA al crear. */
  nombre: string;
  fotoIds: string[];
}

export const idDeFoto = (indice: number): string => `f${indice}`;

const chunk = <T,>(lista: T[], n: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < lista.length; i += n) out.push(lista.slice(i, i + n));
  return out;
};

/**
 * Los grupos de la IA → productos propuestos. Mismas reglas que
 * `attachToDesdeGrupos` (front) y `parseRespuestaGrupos` (backend): ids desconocidos se
 * ignoran, repetidos gana la primera aparición, un grupo más grande que el tope se parte
 * (el primer tramo se queda con el nombre), y toda foto que no cayó en ningún grupo es un
 * producto propio. Perder una foto en silencio es el único resultado prohibido.
 */
export function propuestaDesdeGrupos(
  fotos: FotoLote[],
  grupos: GrupoIaRespuesta[],
  maxPorProducto: number = MAX_IMAGENES_POR_PRODUCTO,
): ProductoPropuesto[] {
  const existentes = new Set(fotos.map((f) => f.id));
  const usados = new Set<string>();
  const out: ProductoPropuesto[] = [];
  let n = 0;
  for (const g of grupos) {
    // Se marca al pasar y no después: un id repetido DENTRO del mismo grupo también es
    // una repetición.
    const ids: string[] = [];
    for (const id of g.ids) {
      if (!existentes.has(id) || usados.has(id)) continue;
      usados.add(id);
      ids.push(id);
    }
    if (ids.length === 0) continue;
    const nombre = (g.nombre ?? '').trim();
    chunk(ids, maxPorProducto).forEach((tramo, i) => {
      n += 1;
      out.push({ id: `prod-${n}`, nombre: i === 0 ? nombre : '', fotoIds: tramo });
    });
  }
  for (const f of fotos) {
    if (usados.has(f.id)) continue;
    n += 1;
    out.push({ id: `prod-${n}`, nombre: '', fotoIds: [f.id] });
  }
  return out;
}

/** Sin IA (una sola foto, o el agrupado falló): cada foto es un producto. */
export const propuestaPlana = (fotos: FotoLote[]): ProductoPropuesto[] =>
  fotos.map((f, i) => ({ id: `prod-${i + 1}`, nombre: '', fotoIds: [f.id] }));

/**
 * "Agregámelo como producto" (singular): TODAS las fotos son de un mismo producto, hasta
 * el tope; lo que sobra se avisa (nunca se descarta en silencio).
 */
export function propuestaUnica(
  fotos: FotoLote[],
  nombre: string,
  maxPorProducto: number = MAX_IMAGENES_POR_PRODUCTO,
): { propuesta: ProductoPropuesto[]; sobrantes: number } {
  if (fotos.length === 0) return { propuesta: [], sobrantes: 0 };
  const ids = fotos.slice(0, maxPorProducto).map((f) => f.id);
  return {
    propuesta: [{ id: 'prod-1', nombre: nombre.trim(), fotoIds: ids }],
    sobrantes: Math.max(0, fotos.length - ids.length),
  };
}

// ── De dónde salen las fotos ─────────────────────────────────────────────

export type FuenteDeFotos =
  | { tipo: 'lote'; loteId: string }
  | { tipo: 'generadas'; imageIds: string[] }
  /** Adjuntos puntuales por su id: una foto de un mensaje anterior ("m1-2") o una que el
   *  agente trajo de una página ("web-…", ADR-2026-10-15-el-agente-abre-paginas-web). */
  | { tipo: 'porId'; adjuntoIds: string[] }
  | { tipo: 'adjuntos' };

/** Lo que la tarjeta lee del input de la herramienta. */
export interface EntradaAgregarProductos {
  lote_id?: unknown;
  image_ids?: unknown;
  adjunto_ids?: unknown;
  nombre?: unknown;
  un_solo_producto?: unknown;
  mensaje?: unknown;
}

const texto = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/**
 * Qué fuente usar, en orden de prioridad: un lote (grande, en memoria), imágenes
 * generadas en el chat (`image_ids`), adjuntos puntuales por id (`adjunto_ids`), o las fotos
 * que el usuario adjuntó a su mensaje
 * (no hace falta pasar nada: la tarjeta las busca en el historial).
 */
export function fuenteDeFotos(entrada: EntradaAgregarProductos | null | undefined): FuenteDeFotos {
  const loteId = texto(entrada?.lote_id);
  if (loteId) return { tipo: 'lote', loteId };
  const ids = Array.isArray(entrada?.image_ids)
    ? entrada!.image_ids.map((x) => texto(x)).filter(Boolean)
    : [];
  if (ids.length > 0) return { tipo: 'generadas', imageIds: Array.from(new Set(ids)) };
  const adjuntoIds = Array.isArray(entrada?.adjunto_ids)
    ? entrada!.adjunto_ids.map((x) => texto(x)).filter(Boolean)
    : [];
  if (adjuntoIds.length > 0) return { tipo: 'porId', adjuntoIds: Array.from(new Set(adjuntoIds)) };
  return { tipo: 'adjuntos' };
}

export const nombrePedido = (entrada: EntradaAgregarProductos | null | undefined): string =>
  texto(entrada?.nombre);

export const esUnSoloProducto = (entrada: EntradaAgregarProductos | null | undefined): boolean =>
  entrada?.un_solo_producto === true;

export const nombreParaMostrar = (p: ProductoPropuesto, indice: number): string =>
  p.nombre.trim() || `Nuevo producto ${indice + 1}`;

/** Lo que entra en esta carga según el plan, y lo que queda afuera (se avisa, nunca se
 *  crea de más ni se descarta en silencio). */
export function recortarAlLimite(
  propuesta: ProductoPropuesto[],
  limite: number,
): { aceptados: ProductoPropuesto[]; sobrantes: ProductoPropuesto[] } {
  const tope = Math.max(0, Math.floor(limite));
  return { aceptados: propuesta.slice(0, tope), sobrantes: propuesta.slice(tope) };
}

export function mensajeLimiteProductos(limite: number, sobrantes: number): string | null {
  if (sobrantes <= 0) return null;
  return `Tu plan permite ${limite} productos por carga: ${sobrantes} quedan afuera. Podés crearlos en otra tanda.`;
}

// ── Resumen al agente ─────────────────────────────────────────────────────

export interface ProductoCreado {
  /** El id de la VARIANTE, que es el `product_id` del catálogo del agente. */
  product_id: string;
  nombre: string;
  fotos: number;
}

export interface ResumenCreacion {
  created: boolean;
  cantidad: number;
  productos: ProductoCreado[];
  fallidos: Array<{ nombre: string; motivo: string }>;
  /** Cuántos quedaron afuera por el límite del plan. */
  fuera_por_plan: number;
}

export function resumenCreacionParaAgente(args: {
  creados: ProductoCreado[];
  fallidos: Array<{ nombre: string; motivo: string }>;
  fueraPorPlan: number;
}): ResumenCreacion {
  return {
    created: args.creados.length > 0,
    cantidad: args.creados.length,
    productos: args.creados,
    fallidos: args.fallidos,
    fuera_por_plan: args.fueraPorPlan,
  };
}

/** El `[registro:]` del historial: los ids nuevos son del catálogo y el agente los puede
 *  usar en el turno siguiente. */
export function registroProductosParaHistorial(r: ResumenCreacion): string {
  const lista = r.productos.map((p) => `"${p.nombre}" (product_id=${p.product_id}, ${p.fotos} foto(s))`).join(' · ');
  const fallidos = r.fallidos.length
    ? ` No se pudieron crear: ${r.fallidos.map((f) => `"${f.nombre}" (${f.motivo})`).join(', ')}.`
    : '';
  const plan = r.fuera_por_plan > 0 ? ` ${r.fuera_por_plan} quedaron afuera por el límite del plan.` : '';
  return `[registro: se crearon ${r.cantidad} producto(s) en el catálogo desde fotos: ${lista || '(ninguno)'}.${fallidos}${plan} Esos product_id ya sirven como reference_images (source "producto").]`;
}
